import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ethers } from 'ethers';
import { TOKEN_LIST } from './constants';
import SimpleLiquidityPoolABI from './abis/SimpleLiquidityPool.json';
import IERC20ABI from './abis/IERC20ABI.json';

// This should be updated with your deployed SimpleLiquidityPool address
const LIQUIDITY_POOL_ADDRESS = '0x911740f80d3BD5bc9886aE4cB6EF9dcBC7B770Bb';

// Helper function to format balances with 2 decimal places
const formatBalance = (balance) => {
  if (!balance || balance === '') return '0';
  
  // Convert to number and format with 2 decimal places
  const numBalance = parseFloat(balance);
  if (isNaN(numBalance)) return '0';
  
  // Return with 2 decimal places
  return numBalance.toFixed(2);
};

// Helper function to format display values without forcing decimal places on whole numbers
const formatDisplayValue = (value) => {
  if (!value || value === '') return '';
  
  const numValue = parseFloat(value);
  if (isNaN(numValue)) return '';
  
  // If it's a whole number, display without decimal
  if (numValue % 1 === 0) {
    return numValue.toString();
  } else {
    // Otherwise keep the decimal places (but limit to reasonable precision)
    return numValue.toString();
  }
};

const SimpleLiquidityPool = ({ account }) => {
  const { t } = useTranslation(['common', 'liquidity', 'defi']);
  const [activeTab, setActiveTab] = useState('add'); // 'add' or 'remove'
  const [token0, setToken0] = useState(TOKEN_LIST[0]);
  const [token1, setToken1] = useState(TOKEN_LIST[1]);
  const [amount0, setAmount0] = useState('');
  const [amount1, setAmount1] = useState('');
  const [liquidityAmount, setLiquidityAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Separate state for pool information and user-specific data
  const [poolInfo, setPoolInfo] = useState({
    reserve0: '0',
    reserve1: '0',
    totalSupply: '0',
    exchangeRate: '1'
  });
  const [userLPBalance, setUserLPBalance] = useState('0');
  
  // Define button style for consistent look
  const buttonStyle = {
    background: 'linear-gradient(to right, #6472F3, #4B5EF7)',
    color: 'white',
    border: 'none',
    borderRadius: '20px',
    fontSize: '18px',
    fontWeight: '600',
    padding: '16px',
    width: '100%',
    cursor: (loading || !account) ? 'not-allowed' : 'pointer',
    marginTop: '20px',
    transition: 'all 0.3s ease',
    textTransform: 'uppercase',
    opacity: (loading || !account) ? 0.5 : 1,
    boxShadow: '0 4px 12px rgba(76, 110, 245, 0.2)'
  };

  // Define token select button style
  const tokenSelectStyle = {
    display: 'flex',
    alignItems: 'center',
    background: 'linear-gradient(to right, #6472F3, #4B5EF7)',
    border: 'none',
    borderRadius: '12px',
    padding: '8px 12px',
    color: 'white',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 2px 8px rgba(76, 110, 245, 0.2)'
  };

  // Calculate the other token amount based on pool ratio
  const calculateOtherAmount = (amount, isToken0, currentPoolInfo) => {
    // Use provided pool info or fallback to state
    const poolData = currentPoolInfo || poolInfo;
    
    if (!poolData || !amount || parseFloat(poolData.reserve0) === 0 || parseFloat(poolData.reserve1) === 0) return '';
    
    const ratio = isToken0 
      ? parseFloat(poolData.reserve1) / parseFloat(poolData.reserve0)
      : parseFloat(poolData.reserve0) / parseFloat(poolData.reserve1);
    
    // Calculate but don't force 2 decimal places
    const calculatedAmount = (parseFloat(amount) * ratio);
    
    // If it's an extremely small number, just return 0
    if (calculatedAmount < 0.000001) return '0';
    
    // Format without forcing decimal places on whole numbers
    return formatDisplayValue(calculatedAmount);
  };

  // Function to handle setting max amount
  const handleSetMaxAmount = () => {
    setLiquidityAmount(userLPBalance);
  };

  // Function to initialize different token default values
  useEffect(() => {
    // Make sure token0 and token1 are different at component initialization
    if (TOKEN_LIST.length >= 2) {
      setToken0(TOKEN_LIST[0]);
      setToken1(TOKEN_LIST[1]);
    }
  }, []);
  
  // Fetch pool information and user LP balance separately
  useEffect(() => {
    const fetchPoolInfo = async () => {
      try {
        const provider = new ethers.providers.Web3Provider(window.ethereum);
        
        const poolContract = new ethers.Contract(
          LIQUIDITY_POOL_ADDRESS,
          SimpleLiquidityPoolABI,
          provider
        );
        
        // Get pool reserves
        const [reserve0, reserve1] = await poolContract.getReserves();
        
        // Get total supply
        const totalSupply = await poolContract.totalSupply();
        
        // Format balances for display
        const formattedReserve0 = formatBalance(ethers.utils.formatUnits(reserve0, token0.decimals));
        const formattedReserve1 = formatBalance(ethers.utils.formatUnits(reserve1, token1.decimals));
        const formattedTotalSupply = formatBalance(ethers.utils.formatUnits(totalSupply, 18));
        
        const exchangeRate = reserve0.gt(0) ? 
          parseFloat(ethers.utils.formatUnits(reserve1, token1.decimals)) / 
          parseFloat(ethers.utils.formatUnits(reserve0, token0.decimals)) : '1';
        
        setPoolInfo({
          reserve0: formattedReserve0,
          reserve1: formattedReserve1,
          totalSupply: formattedTotalSupply,
          exchangeRate: exchangeRate
        });
        
        // Calculate the other token amount based on ratio if needed
        if (amount0 && parseFloat(amount0) > 0 && reserve0.gt(0)) {
          const calculatedAmount1 = calculateOtherAmount(amount0, true, {
            reserve0: formattedReserve0,
            reserve1: formattedReserve1
          });
          if (calculatedAmount1) {
            setAmount1(calculatedAmount1);
          }
        } else if (amount1 && parseFloat(amount1) > 0 && reserve1.gt(0)) {
          const calculatedAmount0 = calculateOtherAmount(amount1, false, {
            reserve0: formattedReserve0,
            reserve1: formattedReserve1
          });
          if (calculatedAmount0) {
            setAmount0(calculatedAmount0);
          }
        }
      } catch (err) {
        console.error('Error fetching pool info:', err);
      }
    };
    
    fetchPoolInfo();
    const interval = setInterval(fetchPoolInfo, 10000); // Refresh every 10 seconds
    
    return () => clearInterval(interval);
  // Add calculateOtherAmount to the dependency array to fix the warning
  }, [token0.address, token1.address, token0.decimals, token1.decimals, amount0, amount1, calculateOtherAmount]);
  
  // Separately fetch user's LP balance when account changes
  useEffect(() => {
    const fetchUserLPBalance = async () => {
      if (!account) {
        setUserLPBalance('0');
        return;
      }
      
      try {
        const provider = new ethers.providers.Web3Provider(window.ethereum);
        
        const poolContract = new ethers.Contract(
          LIQUIDITY_POOL_ADDRESS,
          SimpleLiquidityPoolABI,
          provider
        );
        
        // Get user's LP token balance
        const lpBalance = await poolContract.balanceOf(account);
        
        // Format LP balance
        const formattedLPBalance = formatBalance(ethers.utils.formatUnits(lpBalance, 18));
        console.log(`LP balance for account ${account}: ${formattedLPBalance}`);
        
        setUserLPBalance(formattedLPBalance);
      } catch (err) {
        console.error('Error fetching user LP balance:', err);
        setUserLPBalance('0');
      }
    };
    
    fetchUserLPBalance();
    // Only set interval if account exists
    let interval;
    if (account) {
      interval = setInterval(fetchUserLPBalance, 10000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [account]); // Only depend on account changes

  // With only two tokens, just switch them directly (always ensuring they're different)
  const switchTokens = () => {
    // Simply swap token0 and token1
    const tempToken = token0;
    setToken0(token1);
    setToken1(tempToken);
    
    // Reset input amounts when tokens change
    setAmount0('');
    setAmount1('');
  };

  // Function to clear input fields
  const clearInputs = () => {
    setAmount0('');
    setAmount1('');
  };

  // Record add liquidity transaction
  const recordAddLiquidityTransaction = (tokenAAmount, tokenBAmount, lpTokensReceived, txHash) => {
    if (typeof window.addTransaction === 'function') {
      window.addTransaction({
        type: 'ADD_LIQUIDITY',
        tokenA: token0.symbol,
        tokenB: token1.symbol,
        tokenAAmount: tokenAAmount,
        tokenBAmount: tokenBAmount,
        lpTokens: lpTokensReceived,
        hash: txHash,
        timestamp: Date.now()
      });
    }
  };

  // Record remove liquidity transaction
  const recordRemoveLiquidityTransaction = (tokenAAmount, tokenBAmount, lpTokensBurned, txHash) => {
    if (typeof window.addTransaction === 'function') {
      window.addTransaction({
        type: 'REMOVE_LIQUIDITY',
        tokenA: token0.symbol,
        tokenB: token1.symbol,
        tokenAAmount: tokenAAmount,
        tokenBAmount: tokenBAmount,
        lpTokens: lpTokensBurned,
        hash: txHash,
        timestamp: Date.now()
      });
    }
  };

  const handleAddLiquidity = async () => {
    if (!account || !amount0 || !amount1) return;
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = provider.getSigner();
      
      // Get contract instances
      const poolContract = new ethers.Contract(
        LIQUIDITY_POOL_ADDRESS,
        SimpleLiquidityPoolABI,
        signer
      );
      
      const token0Contract = new ethers.Contract(
        token0.address,
        IERC20ABI,
        signer
      );
      
      const token1Contract = new ethers.Contract(
        token1.address,
        IERC20ABI,
        signer
      );
      
      // Approve tokens
      const amount0Wei = ethers.utils.parseUnits(amount0, token0.decimals);
      const amount1Wei = ethers.utils.parseUnits(amount1, token1.decimals);
      
      const approval0Tx = await token0Contract.approve(LIQUIDITY_POOL_ADDRESS, amount0Wei);
      await approval0Tx.wait();
      
      const approval1Tx = await token1Contract.approve(LIQUIDITY_POOL_ADDRESS, amount1Wei);
      await approval1Tx.wait();
      
      // Calculate minimum amounts (5% slippage)
      const amount0Min = amount0Wei.mul(95).div(100);
      const amount1Min = amount1Wei.mul(95).div(100);
      
      // Get current LP balance before adding liquidity (prefix with underscore since we don't use it directly)
      const _oldLPBalance = await poolContract.balanceOf(account);
      
      // Add liquidity
      const tx = await poolContract.addLiquidity(
        amount0Wei,
        amount1Wei,
        amount0Min,
        amount1Min
      );
      
      const receipt = await tx.wait();
      
      // Get updated LP balance after adding liquidity
      const newLPBalance = await poolContract.balanceOf(account);
      
      // Calculate LP tokens received (using _oldLPBalance with underscore)
      const lpTokensReceived = ethers.utils.formatUnits(newLPBalance.sub(_oldLPBalance), 18);
      
      // Record the transaction
      recordAddLiquidityTransaction(
        amount0, 
        amount1, 
        lpTokensReceived,
        receipt.transactionHash
      );
      
      setSuccess(t('common:success.txSuccess'));
      setAmount0('');
      setAmount1('');
      
      // Update the user's LP balance directly
      setUserLPBalance(formatBalance(ethers.utils.formatUnits(newLPBalance, 18)));
      
    } catch (err) {
      console.error('Error adding liquidity:', err);
      setError(err.message || t('common:errors.networkError'));
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveLiquidity = async () => {
    if (!account || !liquidityAmount) return;
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = provider.getSigner();
      
      const poolContract = new ethers.Contract(
        LIQUIDITY_POOL_ADDRESS,
        SimpleLiquidityPoolABI,
        signer
      );
      
      const liquidityWei = ethers.utils.parseUnits(liquidityAmount, 18);
      
      // Calculate expected amounts based on pool ratio
      const totalSupply = await poolContract.totalSupply();
      const [reserve0, reserve1] = await poolContract.getReserves();
      
      const amount0Expected = liquidityWei.mul(reserve0).div(totalSupply);
      const amount1Expected = liquidityWei.mul(reserve1).div(totalSupply);
      
      // Set minimum expected amounts (5% slippage)
      const amount0Min = amount0Expected.mul(95).div(100);
      const amount1Min = amount1Expected.mul(95).div(100);
      
      // Store expected amounts for the transaction history
      const token0Amount = ethers.utils.formatUnits(amount0Expected, token0.decimals);
      const token1Amount = ethers.utils.formatUnits(amount1Expected, token1.decimals);
      
      // Get current LP balance before removing liquidity (prefix with underscore since we don't use it directly)
      const _oldLPBalance = await poolContract.balanceOf(account);
      
      // Remove liquidity
      const tx = await poolContract.removeLiquidity(
        liquidityWei,
        amount0Min,
        amount1Min
      );
      
      const receipt = await tx.wait();
      
      // Get updated LP balance after removing liquidity
      const newLPBalance = await poolContract.balanceOf(account);
      
      // Record the transaction
      recordRemoveLiquidityTransaction(
        token0Amount,
        token1Amount,
        liquidityAmount,
        receipt.transactionHash
      );
      
      setSuccess(t('common:success.txSuccess'));
      setLiquidityAmount('');
      
      // Update the user's LP balance directly
      setUserLPBalance(formatBalance(ethers.utils.formatUnits(newLPBalance, 18)));
      
    } catch (err) {
      console.error('Error removing liquidity:', err);
      setError(err.message || t('common:errors.networkError'));
    } finally {
      setLoading(false);
    }
  };

  // Handle input changes with consistent formatting
  const handleAmount0Change = (e) => {
    const value = e.target.value;
    // Allow empty string or valid numbers
    if (value === '' || /^[0-9]*\.?[0-9]*$/.test(value)) {
      setAmount0(value);
    }
  };

  const handleAmount1Change = (e) => {
    const value = e.target.value;
    // Allow empty string or valid numbers
    if (value === '' || /^[0-9]*\.?[0-9]*$/.test(value)) {
      setAmount1(value);
    }
  };

  // Calculate pool share percentage
  const calculatePoolShare = () => {
    if (!poolInfo.totalSupply || parseFloat(poolInfo.totalSupply) === 0) {
      return '0';
    }
    
    const share = (parseFloat(userLPBalance) / parseFloat(poolInfo.totalSupply)) * 100;
    return share.toFixed(2); // Show only 2 decimal places
  };

  return (
    <div className="liquidity-container">
      <div className="liquidity-card">
        <div className="liquidity-tabs">
          <button
            className={activeTab === 'add' ? 'active' : ''}
            onClick={() => setActiveTab('add')}
          >
            {t('liquidity:tabs.add')}
          </button>
          <button
            className={activeTab === 'remove' ? 'active' : ''}
            onClick={() => setActiveTab('remove')}
          >
            {t('liquidity:tabs.remove')}
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}
        {!account && (
          <div className="warning-message">
            {t('common:warnings.liquidityConnect')}
          </div>
        )}

        {activeTab === 'add' ? (
          <div className="liquidity-form">
            <div className="token-input-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <label>{token0.symbol}</label>
                <button
                  style={tokenSelectStyle}
                  onClick={switchTokens}
                  disabled={loading || !account}
                  title={t('liquidity:tokens.switchTokens')}
                >
                  {token0.symbol}
                  <span style={{ marginLeft: '6px', fontSize: '12px' }}>▼</span>
                </button>
              </div>
              <input
                type="text"
                inputMode="decimal"
                placeholder="0"
                value={amount0}
                onChange={handleAmount0Change}
                disabled={loading || !account}
              />
              {account && poolInfo && (
                <div className="balance-display">
                  {t('swap:labels.balance')}: {poolInfo.reserve0} {token0.symbol}
                </div>
              )}
            </div>

            {/* Simple, clean implementation of the icons */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              margin: '16px 0', 
              padding: '0 20%'
            }}>
              <div 
                onClick={clearInputs}
                style={{
                  backgroundColor: 'rgba(58, 63, 77, 0.8)',
                  color: '#7c85a2',
                  borderRadius: '50%',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  cursor: 'pointer',
                  fontSize: '18px'
                }}
                title={t('liquidity:tokens.clear')}
              >×</div>
              
              <div 
                onClick={switchTokens} 
                style={{
                  backgroundColor: 'rgba(58, 63, 77, 0.8)',
                  color: '#7c85a2',
                  borderRadius: '50%',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  fontSize: '18px',
                  cursor: 'pointer'
                }}
                title={t('liquidity:tokens.switchTokens')}
              >+</div>
            </div>

            <div className="token-input-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <label>{token1.symbol}</label>
                <button 
                  style={tokenSelectStyle}
                  onClick={switchTokens}
                  disabled={loading || !account}
                  title={t('liquidity:tokens.switchTokens')}
                >
                  {token1.symbol}
                  <span style={{ marginLeft: '6px', fontSize: '12px' }}>▼</span>
                </button>
              </div>
              <input
                type="text"
                inputMode="decimal"
                placeholder="0"
                value={amount1}
                onChange={handleAmount1Change}
                disabled={loading || !account || (poolInfo && parseFloat(poolInfo.reserve0) > 0 && parseFloat(poolInfo.reserve1) > 0)}
              />
              {account && poolInfo && (
                <div className="balance-display">
                  {t('swap:labels.balance')}: {poolInfo.reserve1} {token1.symbol}
                </div>
              )}
            </div>

            <button
              onClick={handleAddLiquidity}
              disabled={loading || !amount0 || !amount1 || !account || amount0 === '0' || amount1 === '0'}
              style={buttonStyle}
              className="liquidity-button"
            >
              {!account ? t('common:buttons.connect') : loading ? t('common:loading.processing') : t('liquidity:add.buttonAdd')}
            </button>
          </div>
        ) : (
          <div className="liquidity-form">
            <div className="lp-balance">
              {t('liquidity:remove.balance', { amount: userLPBalance })}
            </div>

            <div className="token-input-group">
              <div className="input-label-row">
                <label>{t('liquidity:remove.lpTokensToRemove')}</label>
                <button 
                  type="button"
                  className="max-button"
                  onClick={handleSetMaxAmount}
                  disabled={!account || parseFloat(userLPBalance) <= 0}
                >
                  {t('common:buttons.max')}
                </button>
              </div>
              <input
                type="text"
                inputMode="decimal" 
                placeholder="0"
                value={liquidityAmount}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || /^[0-9]*\.?[0-9]*$/.test(value)) {
                    setLiquidityAmount(value);
                  }
                }}
                disabled={loading || !account}
                className={parseFloat(liquidityAmount) > parseFloat(userLPBalance) ? "invalid-input" : ""}
              />
              {parseFloat(liquidityAmount) > parseFloat(userLPBalance) && (
                <div className="error-message input-error">
                  {t('liquidity:remove.exceedsBalance', { amount: userLPBalance })}
                </div>
              )}
            </div>
            
            {parseFloat(liquidityAmount) > 0 && poolInfo && (
              <div className="expected-return">
                <div className="expected-return-title">{t('liquidity:remove.expectedReturn')}</div>
                <div className="expected-tokens">
                  {t('liquidity:remove.tokenReturnAmount', { 
                    amount: formatDisplayValue((parseFloat(liquidityAmount) / parseFloat(poolInfo.totalSupply)) * parseFloat(poolInfo.reserve0)),
                    symbol: token0.symbol
                  })}
                  {t('liquidity:remove.plusToken')}
                  {t('liquidity:remove.tokenReturnAmount', { 
                    amount: formatDisplayValue((parseFloat(liquidityAmount) / parseFloat(poolInfo.totalSupply)) * parseFloat(poolInfo.reserve1)),
                    symbol: token1.symbol
                  })}
                </div>
              </div>
            )}

            <button
              onClick={handleRemoveLiquidity}
              disabled={
                loading || 
                !liquidityAmount || 
                !account || 
                parseFloat(liquidityAmount) <= 0 || 
                parseFloat(liquidityAmount) > parseFloat(userLPBalance)
              }
              style={buttonStyle}
              className="liquidity-button"
            >
              {!account ? t('common:buttons.connect') : loading ? t('common:loading.processing') : 
              (parseFloat(liquidityAmount) > parseFloat(userLPBalance) ? 
                t('liquidity:remove.insufficientBalance') : t('liquidity:remove.buttonRemove'))}
            </button>
          </div>
        )}

        {poolInfo && (
          <div className="pool-info">
            <h4>{t('liquidity:poolInfo.title')}</h4>
            <div>
              <span className="label">{t('liquidity:poolInfo.totalLiquidity')}</span> 
              <span className="value">{poolInfo.totalSupply} LP</span>
            </div>
            <div>
              <span className="label">{t('liquidity:poolInfo.tokenReserve', { token: token0.symbol })}</span>
              <span className="value">{poolInfo.reserve0}</span>
            </div>
            <div>
              <span className="label">{t('liquidity:poolInfo.tokenReserve', { token: token1.symbol })}</span>
              <span className="value">{poolInfo.reserve1}</span>
            </div>
            <div>
              <span className="label">{t('liquidity:poolInfo.exchangeRate')}</span>
              <span className="value">
                {t('liquidity:poolInfo.exchangeRateValue', {
                  fromToken: token0.symbol,
                  rate: formatDisplayValue(poolInfo.exchangeRate),
                  toToken: token1.symbol
                })}
              </span>
            </div>
            {account && parseFloat(userLPBalance) > 0 && (
              <>
                <div>
                  <span className="label">{t('liquidity:poolInfo.yourLpTokens')}</span>
                  <span className="value">{userLPBalance} LP</span>
                </div>
                <div>
                  <span className="label">{t('liquidity:poolInfo.yourPoolShare')}</span>
                  <span className="value">
                    {t('liquidity:poolInfo.poolSharePercent', { percent: calculatePoolShare() })}
                  </span>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SimpleLiquidityPool;